<template>
  <div>
    <dialog-confirm ref="remove-service-provider">
      <div class="text-subtitle-1">
        {{ $t('customer_service_providers_ui.are_you_sure_you_want_to_delete_this_service_provider') }}
      </div>
    </dialog-confirm>
    <v-row>
      <v-col md="6" cols="12">
        <h4>{{ $tc('integrated_service', 2) }}</h4>
        <v-card v-for="(provider, index) in customerServiceProviders" :key="index" class="mb-4" outlined>
          <v-card-title>
            {{ provider.service_provider.name }}
          </v-card-title>
          <v-card-text>
            <v-row v-for="(item, index) in provider.credentials" :key="index">
              <v-col class="credential-key text-sm-right font-weight-bold">{{ index }}</v-col>
              <v-col>{{ item }}</v-col>
            </v-row>
            <v-row>
              <v-col class="text-sm-right font-weight-bold">{{ $t('last_update') }}</v-col>
              <v-col>
                <multi-time-display :time="provider.last_vehicle_data"></multi-time-display>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col>
                <v-btn color="primary" @click="pullData(provider)" block>
                  <v-icon>mdi-cloud-download</v-icon>
                  {{ $t('customer_service_providers_ui.pull_data_now') }}
                </v-btn>
              </v-col>
              <v-col>
                <v-btn color="error" @click="removeServiceProvider(provider)" block>
                  <v-icon>mdi-delete</v-icon>
                  {{ $t('customer_service_providers_ui.remove_service') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col md="6" cols="12">
        <h4>{{ $t('add_service') }}</h4>
        <v-card outlined>
          <v-card-text>
            <v-form id="add-service-provider-form" @submit.prevent="addNewServiceProvider">
              <v-select
                :label="$t('service_provider')"
                v-model="newProvider.serviceProvider"
                :items="serviceProviderOptions"
                @input="fillCredentialsFromTemplate($event)"
                item-value="value"
                item-text="text"
                return-object/>
              <v-textarea
                id="crdentials-input"
                v-model="newProvider.credentials"
                :placeholder="$t('customer_service_providers_ui.enter_credentials_in_json_format')" />
              <v-btn type="submit"
                     color="primary" block>
                <v-icon class="ml-n2">mdi-content-save</v-icon>
                {{ $t('add_service') }}
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import DialogConfirm from '@/components/shared/DialogConfirm'

import { map } from 'lodash'

const credentialTemplates = {
  TracKing: {
    customerId: '',
    username: '',
    password: ''
  },
  EuroScan: {
    accountName: '',
    username: '',
    password: '',
    freezerTempIds: [],
    coolerTempIds: []
  },
  ibright: {
    apiKey: ''
  },
  OrbComm: {
    username: '',
    password: ''
  },
  OrbcommOTP: {
    orgKey: '',
    username: '',
    password: '',
    freezerSensor: 'returnTemp',
    coolerSensor: 'returnTemp2'
  },
  MBKorea: {
    endpoint: ''
  },
  Hermatix: {
    token: '',
    operateAs: ''
  },
  ESolutions: {
    subscriptionId: '',
    clientId: '',
    clientSecret: ''
  },
  Lynx: {
    apiKey: '',
    freezerSensor: 'returnAirTemp1',
    coolerSensor: 'returnAirTemp3',
    vehicleBatchSize: 25,
    maxTimeWindowMinutes: 20
  },
  Sunswap: {
    clientId: '',
    clientSecret: '',
    customerCode: '',
    freezerZone: 'main',
    coolerZone: null,
    backfillEnabled: true
  },
  Navfleet: {
    clientId: '',
    clientSecret: '',
    freezerSensorName: 'Temperature 1',
    coolerSensorName: 'Temperature 3',
    freezerSetpointName: null,
    coolerSetpointName: null,
    maxWindowMinutes: 60
  }
}

export default {
  name: 'CustomerServiceProviders',
  components: {
    MultiTimeDisplay,
    DialogConfirm
  },
  mixins: [ApiUtilitiesMixin],
  props: {
    customer: Object
  },
  data () {
    return {
      isAdding: false,
      customerServiceProviders: [],
      serviceProviders: [],
      newProvider: {
        serviceProvider: null,
        credentials: null
      }
    }
  },
  methods: {
    /**
     * Retrieve the customer's service providers
     */
    fetchCustomerServiceProviders () {
      const requestParams = {
        customer_id: this.customer.id,
        expand: 'service_provider',
        'per-page': 0
      }

      const customerServiceProviderPromise = this.$orm('CustomerServiceProvider').findAll(requestParams)
        .then((resp) => {
          this.customerServiceProviders = resp.getItems()
        })

      const serviceProviderPromise = this.$orm('ServiceProvider').findAll()
        .then((resp) => {
          this.serviceProviders = resp.getItems()
        })

      Promise.all([customerServiceProviderPromise, serviceProviderPromise])
        .catch((error) => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
    },
    /**
     * Send a request to the API to add a new service provider.
     */
    addNewServiceProvider () {
      let parsedJSON = {}
      try {
        parsedJSON = JSON.parse(this.newProvider.credentials)
      } catch (error) {
        this.$store.dispatch(
          'globalMessagingModule/showErrorFlashBanner',
          'customer_service_providers_ui.could_not_parse_credentials'
        )
        throw (error)
      }

      this.isAdding = true

      const newProvider = this.$orm('CustomerServiceProvider').createRecord({
        customer_id: this.customer.id,
        service_provider_id: this.newProvider.serviceProvider.value,
        credentials: parsedJSON
      })
      this.$orm('CustomerServiceProvider').createOrUpdate(newProvider)
        .then((resp) => {
          this.fetchCustomerServiceProviders()
          // Reset the form, too
          this.newProvider.serviceProvider = null
          this.newProvider.credentials = null
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('service_provider')} ${this.$i18n.t('generic_save_success')}`
          )
        })
        .catch((error) => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isAdding = false
        })
    },
    /**
     * Send a request to the API to delete the selected service provider.
     * @param provider
     * @returns {Promise<void>}
     */
    async removeServiceProvider (provider) {
      if (await this.$refs['remove-service-provider'].confirm()) {
        provider.destroy()
          .then((resp) => {
            this.fetchCustomerServiceProviders()
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$i18n.tc('service_provider')} ${this.$i18n.t('generic_delete_success')}`
            )
          })
          .catch((error) => {
            if (error) {
              this.renderErrorFlashBanner(error)
              throw (error)
            }
          })
      }
    },
    /**
     * Initiate a request to pull the latest data for this service provider.
     * @param provider
     */
    pullData (provider) {
      this.$httpAdapter({
        url: this.$orm('CustomerServiceProvider').endpoint + '/' + provider.id + '/pull-data',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).then(resp => {
        this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$tc('customer_service_providers_ui.queued_telematics_pull_successfully')}`
        )
      }).catch((error) => {
        if (error) {
          this.renderHttpAdapterErrorBanner(error)
          throw (error)
        }
      })
    },
    fillCredentialsFromTemplate (serviceProvider) {
      if (credentialTemplates[serviceProvider.text]) {
        this.newProvider.credentials = JSON.stringify(credentialTemplates[serviceProvider.text], null, '    ')
      } else {
        this.newProvider.credentials = null
      }
    }
  },
  mounted () {
    this.fetchCustomerServiceProviders()
  },
  computed: {
    serviceProviderOptions () {
      return map(this.serviceProviders, x => {
        return { text: x.name, value: x.id }
      })
    }
  }
}
</script>

<style scoped>

.credential-key {
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
